import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import { ArrowLeft, Eye, Save, Upload, X, Image as ImageIcon, Sparkles, RotateCw } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { blogsApi, absoluteUploadUrl } from '../api/blogs';
import { htmlToMarkdown } from '../lib/pasteToMarkdown';

const empty = {
  title: '', slug: '', excerpt: '', body: '', cover_image_url: '',
  tags: [], author_name: '', author_photo_url: '', seo_title: '', seo_description: '',
  ai_summary: '',
  status: 'draft',
};

/** Drag-drop + click image uploader for blog cover. */
const CoverImageUploader = ({ value, onChange }) => {
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);
  const { toast } = useToast();

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Only image files are allowed', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Max 5 MB.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const { url } = await blogsApi.uploadImage(file);
      onChange(url);
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const preview = value ? absoluteUploadUrl(value) : '';

  return (
    <div>
      {preview ? (
        <div className="relative group">
          <img src={preview} alt="cover preview" className="w-full h-40 object-cover rounded-lg border border-black/5" />
          <div className="absolute inset-0 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 bg-black/50 rounded-lg transition-opacity">
            <button type="button" onClick={()=>inputRef.current?.click()} data-testid="cover-change-btn" className="inline-flex items-center gap-1.5 bg-white text-neutral-900 text-xs font-semibold px-3 py-1.5 rounded-md">
              <Upload size={12} /> Change
            </button>
            <button type="button" onClick={()=>onChange('')} data-testid="cover-remove-btn" className="inline-flex items-center gap-1.5 bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md">
              <X size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={()=>inputRef.current?.click()}
          onDragOver={(e)=>{e.preventDefault(); setDragOver(true);}}
          onDragLeave={()=>setDragOver(false)}
          onDrop={(e)=>{e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]);}}
          data-testid="cover-upload-dropzone"
          className={`w-full h-40 rounded-lg border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 ${dragOver ? 'border-[#F39200] bg-[#F39200]/5' : 'border-neutral-200 hover:border-[#F39200] hover:bg-[#FAF2E8]/50'}`}
        >
          {uploading ? (
            <>
              <div className="w-6 h-6 border-2 border-[#F39200] border-t-transparent rounded-full animate-spin" />
              <div className="text-xs text-neutral-500">Uploading…</div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-[#F39200]/10 text-[#F39200] flex items-center justify-center">
                <ImageIcon size={20} />
              </div>
              <div className="text-sm font-semibold text-neutral-800">Click or drag image to upload</div>
              <div className="text-[11px] text-neutral-500">JPG · PNG · WEBP · max 5 MB</div>
            </>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        data-testid="cover-file-input"
        className="hidden"
        onChange={(e)=>{ upload(e.target.files?.[0]); e.target.value = ''; }}
      />
    </div>
  );
};

export default function BlogEditorPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [blog, setBlog] = useState(empty);
  const [tagsInput, setTagsInput] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  // Convert pasted rich HTML → markdown so bold/italic/tables/colours survive.
  const handleRichPaste = React.useCallback((e) => {
    const html = e.clipboardData?.getData('text/html');
    if (!html) return; // let plain-text paste happen natively
    e.preventDefault();
    const md = htmlToMarkdown(html);
    if (!md) return;
    const ta = e.target;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    setBlog((b) => {
      const next = b.body.slice(0, start) + md + b.body.slice(end);
      // restore caret after React re-renders
      requestAnimationFrame(() => {
        try {
          ta.focus();
          const pos = start + md.length;
          ta.setSelectionRange(pos, pos);
        } catch (err) {
          // Caret restore is best-effort — textarea may have unmounted.
          console.warn('Caret restore after paste failed:', err?.message);
        }
      });
      return { ...b, body: next };
    });
    toast({ title: 'Formatted paste', description: 'Bold, tables and colours were preserved.' });
  }, [toast]);

  const regenerateSummary = async () => {
    if (!isEdit) {
      toast({ title: 'Save the post first', description: 'AI summary is generated for saved posts.', variant: 'destructive' });
      return;
    }
    setSummarizing(true);
    try {
      const updated = await blogsApi.summarize(id);
      setBlog((b) => ({ ...b, ai_summary: updated.ai_summary || '' }));
      toast({ title: 'AI summary regenerated' });
    } catch (err) {
      toast({ title: 'Summary failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSummarizing(false);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const all = await blogsApi.listAdmin();
        const doc = all.find((b) => b.id === id);
        if (doc) {
          setBlog({ ...empty, ...doc });
          setTagsInput((doc.tags || []).join(', '));
        }
      } finally { setLoading(false); }
    })();
  }, [id, isEdit]);

  const save = async (status) => {
    if (!blog.title.trim() || !blog.body.trim()) {
      toast({ title: 'Title and body are required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...blog,
        status: status || blog.status,
        tags: tagsInput.split(',').map((t) => t.trim()).filter(Boolean),
      };
      let saved;
      if (isEdit) saved = await blogsApi.update(id, payload);
      else saved = await blogsApi.create(payload);
      toast({ title: `Post ${payload.status === 'published' ? 'published' : 'saved as draft'}!` });
      navigate('/admin', { replace: true });
      return saved;
    } catch (err) {
      toast({ title: 'Failed to save', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF2E8]"><div className="w-8 h-8 border-4 border-[#F39200] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid="blog-editor-page">
      <header className="bg-white border-b border-black/5 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between gap-3">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-neutral-700 hover:text-neutral-900">
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview(!preview)} className="inline-flex items-center gap-1.5 text-sm border border-neutral-200 rounded-lg px-3 h-9 hover:border-[#F39200]">
              <Eye size={14} /> {preview ? 'Edit' : 'Preview'}
            </button>
            <button onClick={() => save('draft')} disabled={saving} data-testid="save-draft-btn" className="inline-flex items-center gap-1.5 text-sm border border-neutral-200 rounded-lg px-3 h-9 hover:border-[#F39200] disabled:opacity-60">
              <Save size={14} /> Save draft
            </button>
            <button onClick={() => save('published')} disabled={saving} data-testid="publish-btn" className="inline-flex items-center gap-1.5 bg-[#F39200] hover:bg-[#E08400] text-white text-sm font-semibold rounded-lg px-4 h-9 disabled:opacity-60">
              {saving ? 'Saving…' : 'Publish'}
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-5 lg:px-10 py-8">
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8">
          {preview ? (
            <article className="bg-white rounded-2xl border border-black/5 p-8 min-h-[400px]">
              {blog.cover_image_url && <img src={absoluteUploadUrl(blog.cover_image_url)} alt="" className="w-full h-64 object-cover rounded-xl mb-6" />}
              <h1 className="text-3xl md:text-4xl font-extrabold text-neutral-900">{blog.title || 'Untitled'}</h1>
              {blog.excerpt && <p className="mt-3 text-lg text-neutral-600">{blog.excerpt}</p>}
              <div className="prose prose-neutral max-w-none mt-6 prose-headings:font-extrabold prose-a:text-[#F39200]">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{blog.body || '*Start typing your content…*'}</ReactMarkdown>
              </div>
            </article>
          ) : (
            <div className="space-y-4">
              <input
                value={blog.title}
                onChange={(e)=>setBlog({...blog, title: e.target.value})}
                data-testid="blog-title-input"
                placeholder="Post title"
                className="w-full text-3xl font-extrabold px-0 py-2 border-0 border-b border-black/10 focus:border-[#F39200] outline-none bg-transparent placeholder:text-neutral-400"
              />
              <input
                value={blog.excerpt}
                onChange={(e)=>setBlog({...blog, excerpt: e.target.value})}
                placeholder="Short excerpt (shows in blog list & SEO)"
                className="w-full text-base px-0 py-2 border-0 border-b border-black/10 focus:border-[#F39200] outline-none bg-transparent placeholder:text-neutral-400"
              />
              <div data-color-mode="light" data-testid="blog-body-editor">
                <MDEditor
                  value={blog.body}
                  onChange={(v)=>setBlog({...blog, body: v || ''})}
                  height={520}
                  preview="edit"
                  visibleDragbar={false}
                  textareaProps={{
                    placeholder: 'Write your post here… Paste from Google Docs / Word / any webpage — bold, italics, headings, tables, colours and links are preserved automatically.',
                    'data-testid': 'blog-body-textarea',
                    onPaste: handleRichPaste,
                  }}
                />
                <div className="mt-2 text-[11px] text-neutral-500" data-testid="paste-hint">
                  Tip: paste (Ctrl/Cmd + V) directly from Google Docs, Word, Notion or any webpage — formatting, tables and colours are preserved.
                </div>
              </div>
            </div>
          )}

          <aside className="space-y-5">
            <div className="bg-white rounded-2xl border border-black/5 p-5">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">URL slug</div>
              <input
                value={blog.slug}
                onChange={(e)=>setBlog({...blog, slug: e.target.value})}
                data-testid="blog-slug-input"
                placeholder="auto-generated from title"
                className="w-full h-10 px-3 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm"
              />
              <div className="mt-1 text-[11px] text-neutral-500">/blog/<span className="font-semibold">{blog.slug || 'auto'}</span></div>
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-5">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">Cover image</div>
              <CoverImageUploader
                value={blog.cover_image_url}
                onChange={(url)=>setBlog({...blog, cover_image_url: url})}
              />
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-5">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3">Tags</div>
              <input value={tagsInput} onChange={(e)=>setTagsInput(e.target.value)} placeholder="GST, VPOB, Amazon" className="w-full h-10 px-3 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm" />
              <div className="mt-1 text-[11px] text-neutral-500">Comma-separated</div>
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-5 space-y-3">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Author</div>
              <input value={blog.author_name} onChange={(e)=>setBlog({...blog, author_name: e.target.value})} placeholder="Author name" className="w-full h-10 px-3 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm" />
              <input value={blog.author_photo_url} onChange={(e)=>setBlog({...blog, author_photo_url: e.target.value})} placeholder="Author photo URL" className="w-full h-10 px-3 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm" />
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-5 space-y-3">
              <div className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">SEO</div>
              <input value={blog.seo_title} onChange={(e)=>setBlog({...blog, seo_title: e.target.value})} placeholder="SEO title (falls back to post title)" className="w-full h-10 px-3 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm" />
              <textarea value={blog.seo_description} onChange={(e)=>setBlog({...blog, seo_description: e.target.value})} rows={3} placeholder="SEO description (155-160 chars)" className="w-full px-3 py-2 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm" />
            </div>
            <div className="bg-white rounded-2xl border border-black/5 p-5 space-y-3" data-testid="ai-summary-card">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#F39200] font-semibold">
                  <Sparkles size={12} /> AI summary
                </div>
                <button
                  type="button"
                  onClick={regenerateSummary}
                  disabled={summarizing || !isEdit}
                  data-testid="regenerate-summary-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-700 border border-neutral-200 rounded-md px-2.5 h-8 hover:border-[#F39200] hover:text-[#F39200] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCw size={12} className={summarizing ? 'animate-spin' : ''} />
                  {summarizing ? 'Generating…' : (blog.ai_summary ? 'Regenerate' : 'Generate')}
                </button>
              </div>
              <textarea
                value={blog.ai_summary || ''}
                onChange={(e)=>setBlog({...blog, ai_summary: e.target.value})}
                rows={4}
                data-testid="ai-summary-textarea"
                placeholder={isEdit ? 'Click Generate to create a 3-sentence TL;DR shown at the top of the post. You can also edit it manually.' : 'Save the post first, then click Generate to create a TL;DR.'}
                className="w-full px-3 py-2 rounded-lg border border-black/10 focus:border-[#F39200] outline-none text-sm leading-relaxed"
              />
              <div className="text-[11px] text-neutral-500">Auto-generated on publish when empty. Editable anytime.</div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
